import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import type { WaterfallStep } from '@jr2os/shared'
import { formatCents } from '../../../lib/money'
import { LegendDot } from './LegendDot'

// Colores waterfall
const WF_SUBTOTAL = '#2563eb' // blue-600 (saturado)
const WF_POSITIVE = '#16a34a' // emerald-600
const WF_NEGATIVE = '#dc262680' // red translúcido (50%)

interface WaterfallChartRow {
  label: string
  base: number
  delta: number
  /** Para mostrar en el tooltip / label. */
  rawValue: number
  cumulative: number
  type: WaterfallStep['type']
  /** Color del segmento visible. */
  color: string
}

export function WaterfallChart({
  steps,
  taxBooked,
}: {
  steps: WaterfallStep[]
  taxBooked: boolean
}) {
  const { t } = useTranslation('accounting')
  const chartData = useMemo<WaterfallChartRow[]>(() => {
    return steps.map((s) => {
      const previousCumulative = s.cumulative - s.value
      // Para subtotales / start mostramos la barra desde 0 hasta cumulative.
      const isAnchor = s.type === 'subtotal' || s.type === 'start' || s.type === 'end'
      const base = isAnchor ? 0 : Math.min(previousCumulative, s.cumulative)
      const delta = isAnchor ? Math.max(0, s.cumulative) : Math.abs(s.value)
      let color: string
      if (isAnchor) color = WF_SUBTOTAL
      else if (s.type === 'positive') color = WF_POSITIVE
      else color = WF_NEGATIVE
      return {
        label: s.label,
        base,
        delta,
        rawValue: s.value,
        cumulative: s.cumulative,
        type: s.type,
        color,
      }
    })
  }, [steps])

  if (steps.length === 0) {
    return (
      <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
        <h2 className="font-semibold t-primary mb-3">{t('waterfall.title')}</h2>
        <p className="text-sm t-muted">{t('waterfall.noData')}</p>
      </section>
    )
  }

  return (
    <section className="rounded-xl border border-[var(--border)] bg-[var(--bg-input-soft)] p-4">
      <h2 className="font-semibold t-primary mb-3">{t('waterfall.title')}</h2>
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <BarChart data={chartData} margin={{ top: 30, right: 16, bottom: 50, left: 60 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--border)"
              strokeOpacity={0.35}
              vertical={false}
            />
            <XAxis
              tickLine={false}
              axisLine={false}
              dataKey="label"
              stroke="var(--text-muted, #64748b)"
              fontSize={11}
              angle={-25}
              textAnchor="end"
              interval={0}
              height={70}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              stroke="var(--text-muted, #64748b)"
              fontSize={11}
              tickFormatter={(v: number) => formatCents(v)}
            />
            <Tooltip content={<WaterfallTooltip />} cursor={{ fill: 'var(--bg-hover)' }} />
            <Bar dataKey="base" stackId="wf" fill="transparent" />
            <Bar dataKey="delta" stackId="wf" radius={[4, 4, 0, 0]}>
              {chartData.map((row, i) => (
                <Cell key={i} fill={row.color} />
              ))}
              <LabelList
                dataKey="rawValue"
                position="top"
                fontSize={10}
                fill="var(--text-secondary, #94a3b8)"
                formatter={(v: number) => formatCents(v)}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs t-secondary">
        <LegendDot color={WF_SUBTOTAL} label={t('waterfall.subtotal')} />
        <LegendDot color={WF_POSITIVE} label={t('waterfall.income')} />
        <LegendDot color={WF_NEGATIVE} label={t('waterfall.expense')} />
      </div>
      {/* Sin la 630 contabilizada el resultado es ANTES de impuestos: que no parezca que no hay. */}
      {!taxBooked && <p className="mt-2 text-xs t-muted">{t('waterfall.taxNote')}</p>}
    </section>
  )
}

interface WaterfallTooltipPayloadItem {
  dataKey: string
  value: number
  payload: WaterfallChartRow
}

function WaterfallTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: WaterfallTooltipPayloadItem[]
}) {
  const { t } = useTranslation('accounting')
  if (!active || !payload || payload.length === 0) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-input)] p-3 text-xs shadow-lg">
      <div className="font-semibold t-primary mb-1">{row.label}</div>
      <div className="t-secondary">
        {t('waterfall.value')} <span className="font-mono">{formatCents(row.rawValue)}</span>
      </div>
      <div className="t-secondary">
        {t('waterfall.cumulative')} <span className="font-mono">{formatCents(row.cumulative)}</span>
      </div>
    </div>
  )
}
